import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { chatAPI } from '../api/axios';

// Add CSS animation for loading spinners
const spinKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject the keyframes into the document head
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = spinKeyframes;
  document.head.appendChild(style);
}

const RagManagement = () => {
  const navigate = useNavigate();
  const [urls, setUrls] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  // eslint-disable-next-line no-unused-vars
  const [scrapedData, setScrapedData] = useState([]);
  const [selectedTab, setSelectedTab] = useState('databases');
  const [databases, setDatabases] = useState([]);
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isBulkIngesting, setIsBulkIngesting] = useState(false);
  const [searchUrl, setSearchUrl] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [highlightedUrl, setHighlightedUrl] = useState(null);
  const [isDatabasesLoading, setIsDatabasesLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    fetchScrapedData();
    fetchDatabases();
  }, []);

  const fetchDatabases = async () => {
    setIsDatabasesLoading(true);
    try {
      console.log('📊 Fetching RAG databases...');
      const response = await chatAPI.getRagDatabases();
      console.log('📊 RAG databases response:', response.data);
      setDatabases(response.data.databases || []);
      if (response.data.databases?.length > 0) {
        setMessage({ 
          text: `Successfully loaded ${response.data.databases.length} databases with ${response.data.databases.reduce((total, db) => total + db.count, 0)} total URLs`, 
          type: 'success' 
        });
      } else {
        setMessage({ 
          text: 'No databases found. This could be normal if no content has been ingested yet.', 
          type: 'info' 
        });
      }
    } catch (error) {
      console.error('❌ Error fetching databases:', error);
      console.error('❌ Error response:', error.response?.data);
      setMessage({ 
        text: 'Error loading databases: ' + (error.response?.data?.error || error.message), 
        type: 'error' 
      });
      setDatabases([]);
    } finally {
      setIsDatabasesLoading(false);
    }
  };

  const handleGoToDatabase = (databaseName, targetUrl) => {
    // Expand the target database
    setSelectedDatabase(databaseName);
    
    // Highlight the specific URL
    setHighlightedUrl(targetUrl);
    
    // Scroll to the database section
    setTimeout(() => {
      const element = document.getElementById(`database-${databaseName}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
    
    // Clear highlight after 5 seconds
    setTimeout(() => {
      setHighlightedUrl(null);
    }, 5000);
    
    setMessage({ 
      text: `Navigated to ${databaseName} database and highlighted the URL`, 
      type: 'success' 
    });
  };

  const handleSearchUrl = async () => {
    if (!searchUrl.trim()) {
      setMessage({ 
        text: 'Please enter a URL to search for', 
        type: 'warning' 
      });
      return;
    }
    
    setIsSearching(true);
    setSearchResults([]);
    
    try {
      console.log('🔍 Searching for URL:', searchUrl);
      const response = await chatAPI.searchInDatabase(searchUrl);
      console.log('🔍 Search results:', response.data);
      
      if (response.data.results && response.data.results.length > 0) {
        setSearchResults(response.data.results);
        setMessage({ 
          text: `Found ${response.data.results.length} matching URLs`, 
          type: 'success' 
        });
      } else {
        setSearchResults([]);
        setMessage({ 
          text: 'No matching URLs found in the databases', 
          type: 'info' 
        });
      }
    } catch (error) {
      console.error('❌ Error searching databases:', error);
      console.error('❌ Search error response:', error.response?.data);
      setSearchResults([]);
      
      // Improved error message handling
      let errorMessage = 'Error searching databases';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setMessage({ 
        text: errorMessage, 
        type: 'error' 
      });
    } finally {
      setIsSearching(false);
    }
  };

  const closeMessage = () => {
    setMessage({ text: '', type: '' });
  };

  const handleDeleteFromDatabase = async (dbName, url) => {
    if (!window.confirm(`Delete ${url} from ${dbName} database?`)) return;
    
    try {
      const encodedUrl = encodeURIComponent(url);
      await chatAPI.deleteFromDatabase(dbName, encodedUrl);
      setMessage({ text: `Successfully deleted URL from ${dbName}`, type: 'success' });
      fetchDatabases();
    } catch (error) {
      console.error('Error deleting from database:', error);
      setMessage({ text: error.response?.data?.error || 'Failed to delete from database', type: 'error' });
    }
  };

  const fetchScrapedData = async () => {
    try {
      // Use localStorage directly for scraped content
      const localData = JSON.parse(localStorage.getItem('scrapedContent') || '[]');
      console.log('Local scraped data:', localData);
      
      const formattedData = localData.map((item, index) => {
        try {
          // Generate filename like: www.jazeeraairways.com_en-at_about-us.txt
          const urlObj = new URL(item.url);
          let filename = urlObj.hostname.replace(/^www\./, '');
          
          // Extract path and convert to filename format
          const pathParts = urlObj.pathname.split('/').filter(part => part.length > 0);
          if (pathParts.length > 0) {
            filename += '_' + pathParts.join('_').replace(/[^a-zA-Z0-9_-]/g, '-');
          }
          
          // Add .txt extension
          filename += '.txt';
          
          return {
            filename: filename,
            size: item.content.length,
            url: item.url,
            content: item.content,
            title: item.title || 'No Title'
          };
        } catch (urlError) {
          return {
            filename: `scraped_${index + 1}_invalid_url.txt`,
            size: item.content.length,
            url: item.url,
            content: item.content,
            title: item.title || 'No Title'
          };
        }
      });
      
      setScrapedData(formattedData);
    } catch (error) {
      console.error('Error fetching scraped data:', error);
      setMessage({ text: 'Error loading scraped data', type: 'error' });
    }
  };

  const handleScrapeAndIngest = async () => {
    if (!urls.trim()) {
      setMessage({ text: 'Please enter at least one URL', type: 'error' });
      return;
    }

    setIsLoading(true);
    setMessage({ text: 'Starting scrape and ingest process...', type: 'info' });

    try {
      const urlList = urls.split('\n').filter(url => url.trim());
      
      const response = await chatAPI.scrapeAndIngest({
        urls: urlList
      });
      
      const { summary } = response.data;
      
      if (summary.successful > 0) {
        setMessage({ 
          text: `Scrape & Ingest completed! ${summary.successful}/${summary.total} URLs processed successfully.`, 
          type: 'success' 
        });
        setUrls(''); // Clear URLs after successful processing
        fetchDatabases(); // Refresh databases
      } else {
        setMessage({ 
          text: `Scrape & Ingest failed. ${summary.failed} URLs failed to process.`, 
          type: 'error' 
        });
      }
    } catch (error) {
      console.error('Scrape and ingest error:', error);
      setMessage({ 
        text: error.response?.data?.error || 'Error during scrape and ingest process', 
        type: 'error' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleBulkScrape = async () => {
    if (!urls.trim()) {
      setMessage({ text: 'Please enter at least one URL', type: 'error' });
      return;
    }

    setIsLoading(true);
    setMessage({ text: 'Starting scraping process...', type: 'info' });

    try {
      const urlList = urls.split('\n').filter(url => url.trim());
      const scrapedResults = [];
      
      for (let i = 0; i < urlList.length; i++) {
        const url = urlList[i].trim();
        setMessage({ text: `Scraping ${i + 1}/${urlList.length}: ${url}`, type: 'info' });
        
        try {
          const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(url)}`;
          const response = await fetch(proxyUrl);
          const html = await response.text();
          
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          // Extract title
          const titleElement = doc.querySelector('title');
          const title = titleElement ? titleElement.textContent.trim() : 'No Title';
          
          // Remove script and style elements
          const scripts = doc.querySelectorAll('script, style, nav, header, footer');
          scripts.forEach(el => el.remove());
          
          const textContent = doc.body ? doc.body.innerText || doc.body.textContent || '' : '';
          
          if (textContent.trim().length > 100) {
            // Format content with title and URL
            const formattedContent = `Title: ${title}\nURL: ${url}\n\n${textContent.substring(0, 8000).trim()}`;
            
            scrapedResults.push({
              url: url,
              content: formattedContent,
              title: title,
              success: true,
              timestamp: new Date().toISOString()
            });
          } else {
            scrapedResults.push({ url, error: 'No meaningful content found', success: false });
          }
        } catch (error) {
          scrapedResults.push({ url, error: error.message, success: false });
        }
        
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      const successful = scrapedResults.filter(r => r.success).length;
      
      if (successful > 0) {
        const existingData = JSON.parse(localStorage.getItem('scrapedContent') || '[]');
        const newData = [...existingData, ...scrapedResults.filter(r => r.success)];
        localStorage.setItem('scrapedContent', JSON.stringify(newData));
      }
      
      setMessage({ 
        text: `Scraping completed! ${successful}/${urlList.length} URLs successful.`, 
        type: successful > 0 ? 'success' : 'error'
      });
      
      fetchScrapedData();
    } catch (error) {
      setMessage({ text: `Error: ${error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };



  // eslint-disable-next-line no-unused-vars
  const handleViewFile = (file) => {
    const newWindow = window.open('', '_blank');
    newWindow.document.write(`
      <html>
        <head>
          <title>${file.filename}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
            h1 { color: #004a97; border-bottom: 2px solid #004a97; padding-bottom: 10px; }
            pre { background: #f5f5f5; padding: 15px; border-radius: 5px; white-space: pre-wrap; }
            .url { color: #666; font-size: 14px; margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <h1>${file.filename}</h1>
          <div class="url">Source: ${file.url}</div>
          <pre>${file.content}</pre>
        </body>
      </html>
    `);
    newWindow.document.close();
  };

  // eslint-disable-next-line no-unused-vars
  const handleDeleteFile = (file) => {
    if (!window.confirm(`Are you sure you want to delete ${file.filename}?`)) {
      return;
    }

    try {
      const localData = JSON.parse(localStorage.getItem('scrapedContent') || '[]');
      const updatedData = localData.filter(item => item.url !== file.url);
      localStorage.setItem('scrapedContent', JSON.stringify(updatedData));
      
      setMessage({ text: `Successfully deleted ${file.filename}`, type: 'success' });
      fetchScrapedData();
    } catch (error) {
      console.error('Error deleting file:', error);
      setMessage({ text: 'Error deleting file from local storage', type: 'error' });
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleBulkIngest = async () => {
    const localData = JSON.parse(localStorage.getItem('scrapedContent') || '[]');
    if (localData.length === 0) {
      setMessage({ text: 'No content to ingest', type: 'error' });
      return;
    }
    
    if (!window.confirm(`Ingest ${localData.length} scraped items to vector databases?`)) return;
    
    setIsBulkIngesting(true);
    setMessage({ text: 'Processing bulk ingestion...', type: 'info' });
    
    try {
      const response = await chatAPI.bulkIngest({
        content_items: localData.map(item => ({
          url: item.url,
          content: item.content
        }))
      });
      
      const { summary } = response.data;
      
      if (summary.successful > 0) {
        setMessage({ 
          text: `Bulk ingestion completed! ${summary.successful}/${summary.total} URLs ingested successfully.`, 
          type: 'success' 
        });
        localStorage.removeItem('scrapedContent');
        fetchScrapedData();
        fetchDatabases();
      } else {
        setMessage({ 
          text: `Bulk ingestion failed. ${summary.failed} items failed to ingest.`, 
          type: 'error' 
        });
      }
    } catch (error) {
      console.error('Bulk ingestion error:', error);
      setMessage({ text: 'Error during bulk ingestion', type: 'error' });
    } finally {
      setIsBulkIngesting(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleIngestSingleFile = async (file) => {
    if (!window.confirm(`Ingest ${file.filename} to vector database?`)) return;
    
    try {
      const response = await chatAPI.directIngest({
        url: file.url,
        content: file.content
      });
      
      setMessage({ 
        text: `Successfully ingested ${file.filename} to ${response.data.database}`, 
        type: 'success' 
      });
      
      // Remove from localStorage after successful ingest
      const localData = JSON.parse(localStorage.getItem('scrapedContent') || '[]');
      const updatedData = localData.filter(item => item.url !== file.url);
      localStorage.setItem('scrapedContent', JSON.stringify(updatedData));
      fetchScrapedData();
      fetchDatabases();
    } catch (error) {
      console.error('Ingest error:', error);
      setMessage({ 
        text: error.response?.data?.error || 'Failed to ingest content', 
        type: 'error' 
      });
    }
  };

  // Filter databases
  const arabicDatabases = databases.filter(db => 
    db.name.includes('Arabic') || 
    db.name === 'Arabic' ||
    ['Saudi_Arabic', 'UAE_Arabic', 'Qatar_Arabic', 'Kuwait_Arabic', 'Bahrain_Arabic',
     'Oman_Arabic', 'Jordan_Arabic', 'Lebanon_Arabic', 'Iraq_Arabic', 'Egypt_Arabic',
     'Turkey_Arabic', 'Iran_Arabic', 'Pakistan_Arabic', 'Bangladesh_Arabic',
     'Kazakhstan_Arabic', 'Kyrgyzstan_Arabic', 'Uzbekistan_Arabic', 'Tajikistan_Arabic',
     'Nepal_Arabic', 'Sri_Lanka_Arabic', 'Ethiopia_Arabic', 'Georgia_Arabic',
     'Austria_Arabic', 'Azerbaijan_Arabic', 'Bosnia_and_Herzegovina_Arabic',
     'Czech_Republic_Arabic', 'Russia_Arabic', 'UK_AR'].includes(db.name)
  );

  const englishDatabases = databases.filter(db => 
    db.name.startsWith('EN_') || db.name === 'English'
  );

  const DatabaseCard = ({ db }) => (
    <div 
      id={`database-${db.name}`}
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        marginBottom: '12px',
        overflow: 'hidden'
      }}>
      <div 
        onClick={() => setSelectedDatabase(selectedDatabase === db.name ? null : db.name)}
        style={{
          padding: '12px 16px',
          backgroundColor: '#f8fafc',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <div>
          <h4 style={{ margin: 0, color: '#2d3748', fontSize: '14px' }}>{db.name}</h4>
          <p style={{ margin: '2px 0 0', color: '#718096', fontSize: '12px' }}>
            {db.count} URLs
          </p>
        </div>
        <span style={{ fontSize: '14px', color: '#4a5568' }}>
          {selectedDatabase === db.name ? '▼' : '▶'}
        </span>
      </div>
      
      {selectedDatabase === db.name && db.urls.length > 0 && (
        <div style={{ padding: '12px', maxHeight: '200px', overflowY: 'auto' }}>
          {db.urls.map((url, urlIndex) => (
            <div key={urlIndex} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              padding: '8px 0',
              borderBottom: urlIndex < db.urls.length - 1 ? '1px solid #e2e8f0' : 'none',
              backgroundColor: highlightedUrl === url ? '#fbbf24' : 'transparent',
              borderRadius: highlightedUrl === url ? '6px' : '0',
              border: highlightedUrl === url ? '2px solid #f59e0b' : 'none',
              boxShadow: highlightedUrl === url ? '0 0 10px rgba(251, 191, 36, 0.5)' : 'none',
              animation: highlightedUrl === url ? 'pulse 2s infinite' : 'none',
              transition: 'all 0.3s ease'
            }}>
              <div style={{ flex: 1, marginRight: '8px' }}>
                <a 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    fontSize: '11px', 
                    color: '#2563eb', 
                    textDecoration: 'none',
                    wordBreak: 'break-all',
                    lineHeight: '1.4'
                  }}
                  onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                >
                  {url}
                </a>
              </div>
              <button
                onClick={() => handleDeleteFromDatabase(db.name, url)}
                style={{
                  backgroundColor: '#fee2e2',
                  color: '#b91c1c',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  fontSize: '10px',
                  flexShrink: 0,
                  marginTop: '2px'
                }}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
      
      {selectedDatabase === db.name && db.urls.length === 0 && (
        <div style={{ padding: '12px', color: '#718096', fontStyle: 'italic', fontSize: '12px' }}>
          No URLs found
        </div>
      )}
    </div>
  );

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.02); }
            100% { transform: scale(1); }
          }
        `}
      </style>
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#f0f4f8',
        display: 'flex',
        flexDirection: 'column'
      }}>
      <div style={{
        background: 'linear-gradient(90deg, #004a97 0%, #0066cc 100%)',
        padding: '20px 30px',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{
            height: '40px',
            width: '120px',
            marginRight: '15px',
            backgroundColor: '#004a97',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            fontSize: '16px',
            borderRadius: '4px'
          }}>
            J9 AI Assistant
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>RAG Management</h1>
            <p style={{ margin: '5px 0 0', opacity: 0.8 }}>Scrape and manage content for Retrieval-Augmented Generation</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/admin/controlpanel')}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease'
          }}
        >
          ← Back to Admin
        </button>
      </div>

      <div style={{ 
        flex: 1, 
        padding: '30px',
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%'
      }}>
        {message.text && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '20px',
            borderRadius: '8px',
            backgroundColor: message.type === 'error' ? '#fee2e2' : message.type === 'warning' ? '#fef3c7' : message.type === 'info' ? '#dbeafe' : '#d1fae5',
            color: message.type === 'error' ? '#b91c1c' : message.type === 'warning' ? '#d97706' : message.type === 'info' ? '#1d4ed8' : '#047857',
            border: `1px solid ${message.type === 'error' ? '#fecaca' : message.type === 'warning' ? '#fed7aa' : message.type === 'info' ? '#bfdbfe' : '#a7f3d0'}`,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span>{message.text}</span>
            <button
              onClick={closeMessage}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '0',
                marginLeft: '10px',
                color: 'inherit',
                opacity: 0.7,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '1'}
              onMouseLeave={(e) => e.target.style.opacity = '0.7'}
            >
              ×
            </button>
          </div>
        )}

        <div style={{
          display: 'flex',
          marginBottom: '20px',
          borderBottom: '1px solid #e2e8f0',
        }}>
          <button
            onClick={() => setSelectedTab('databases')}
            style={{
              padding: '12px 24px',
              backgroundColor: selectedTab === 'databases' ? '#004a97' : 'transparent',
              color: selectedTab === 'databases' ? 'white' : '#4a5568',
              border: 'none',
              borderBottom: selectedTab === 'databases' ? '3px solid #ffd966' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: selectedTab === 'databases' ? 'bold' : 'normal',
              transition: 'all 0.2s ease'
            }}
          >
            Vector Databases
          </button>

          <button
            onClick={() => setSelectedTab('scrapeIngest')}
            style={{
              padding: '12px 24px',
              backgroundColor: selectedTab === 'scrapeIngest' ? '#004a97' : 'transparent',
              color: selectedTab === 'scrapeIngest' ? 'white' : '#4a5568',
              border: 'none',
              borderBottom: selectedTab === 'scrapeIngest' ? '3px solid #ffd966' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: selectedTab === 'scrapeIngest' ? 'bold' : 'normal',
              transition: 'all 0.2s ease'
            }}
          >
            Scrape & Ingest
          </button>
        </div>

        {selectedTab === 'scrapeIngest' && (
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
            marginBottom: '30px'
          }}>
            <h2 style={{ margin: '0 0 16px', fontSize: '20px', color: '#2d3748' }}>Scrape & Ingest Directly</h2>
            <p style={{ margin: '0 0 16px', color: '#718096' }}>
              Enter URLs to scrape content and automatically ingest to vector databases.
            </p>
            
            <textarea
              value={urls}
              onChange={(e) => setUrls(e.target.value)}
              placeholder="https://www.jazeeraairways.com/about-us&#10;https://www.jazeeraairways.com/destinations&#10;https://www.jazeeraairways.com/services"
              style={{
                width: '100%',
                minHeight: '150px',
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                marginBottom: '16px',
                fontFamily: 'monospace'
              }}
            />
            
            <button
              onClick={handleScrapeAndIngest}
              disabled={isLoading}
              style={{
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '12px 24px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.7 : 1,
                fontWeight: 'bold',
                transition: 'all 0.2s ease',
                marginRight: '10px'
              }}
            >
              {isLoading ? 'Processing...' : '🚀 Scrape & Ingest'}
            </button>
            
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#f0f9ff',
              borderRadius: '6px',
              border: '1px solid #bae6fd'
            }}>
              <h4 style={{ margin: '0 0 8px', color: '#0369a1' }}>ℹ️ How it works:</h4>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#0369a1' }}>
                <li>Scrapes content from each URL</li>
                <li>Automatically ingests to appropriate vector database</li>
                <li>No intermediate storage - direct to database</li>
                <li>Perfect for fast_vectors multi-folder ingestion</li>
              </ul>
            </div>
          </div>
        )}

        {selectedTab === 'databases' && (
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
            marginBottom: '30px'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '20px' 
            }}>
              <h2 style={{ margin: '0', fontSize: '20px', color: '#2d3748' }}>Vector Databases</h2>
              <button
                onClick={() => fetchDatabases()}
                style={{
                  backgroundColor: '#004a97',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#0066cc'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#004a97'}
              >
                🔄 Refresh
              </button>
            </div>
            
            {/* URL Search */}
            <div style={{
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#374151' }}>🔍 Search URL in Databases</h4>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={searchUrl}
                  onChange={(e) => setSearchUrl(e.target.value)}
                  placeholder="Enter URL to search (e.g., https://www.jazeeraairways.com/en-kw/baggage)"
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <button
                  onClick={handleSearchUrl}
                  disabled={isSearching}
                  style={{
                    backgroundColor: isSearching ? '#93c5fd' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    cursor: isSearching ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {isSearching && (
                    <div style={{
                      width: '14px',
                      height: '14px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                  )}
                  {isSearching ? 'Searching...' : 'Search'}
                </button>
                <button
                  onClick={() => { setSearchUrl(''); setSearchResults([]); }}
                  style={{
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Clear
                </button>
              </div>
              
              {/* Search Results */}
              {searchResults.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <h5 style={{ margin: '0 0 8px', fontSize: '13px', color: '#059669' }}>✅ Found in {searchResults.length} database(s):</h5>
                  {searchResults.map((result, index) => (
                    <div key={index} style={{
                      padding: '6px 10px',
                      backgroundColor: '#d1fae5',
                      border: '1px solid #a7f3d0',
                      borderRadius: '4px',
                      marginBottom: '4px',
                      fontSize: '12px',
                      color: '#047857',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span>
                        📊 <strong>{result.database}</strong> - {result.url.length > 60 ? result.url.substring(0, 60) + '...' : result.url}
                      </span>
                      <button
                        onClick={() => handleGoToDatabase(result.database, result.url)}
                        style={{
                          backgroundColor: '#059669',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '10px',
                          fontWeight: 'bold'
                        }}
                      >
                        Go to DB
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Loading State */}
            {isDatabasesLoading && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '40px',
                color: '#64748b'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid #e2e8f0',
                  borderTop: '3px solid #3b82f6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '12px'
                }}></div>
                Loading databases...
              </div>
            )}
            
            {/* Databases Grid */}
            {!isDatabasesLoading && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
              {/* Arabic Databases Column */}
              <div>
                <h3 style={{ 
                  margin: '0 0 16px', 
                  fontSize: '16px', 
                  color: '#2d3748',
                  padding: '12px 16px',
                  backgroundColor: '#fef2f2',
                  borderRadius: '8px',
                  borderLeft: '4px solid #dc2626',
                  textAlign: 'center'
                }}>Arabic Databases (العربية) - {arabicDatabases.length} DBs</h3>
                
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {arabicDatabases.map((db, index) => (
                    <DatabaseCard key={index} db={db} />
                  ))}
                  
                  {arabicDatabases.length === 0 && (
                    <p style={{ color: '#718096', fontStyle: 'italic', textAlign: 'center' }}>No Arabic databases found</p>
                  )}
                </div>
              </div>
              
              {/* English Databases Column */}
              <div>
                <h3 style={{ 
                  margin: '0 0 16px', 
                  fontSize: '16px', 
                  color: '#2d3748',
                  padding: '12px 16px',
                  backgroundColor: '#eff6ff',
                  borderRadius: '8px',
                  borderLeft: '4px solid #2563eb',
                  textAlign: 'center'
                }}>English Databases - {englishDatabases.length} DBs</h3>
                
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {englishDatabases.map((db, index) => (
                    <DatabaseCard key={index} db={db} />
                  ))}
                  
                  {englishDatabases.length === 0 && (
                    <p style={{ color: '#718096', fontStyle: 'italic', textAlign: 'center' }}>No English databases found</p>
                  )}
                </div>
              </div>
            </div>
            )}
          </div>
        )}


      </div>

      <div style={{
        backgroundColor: '#004a97',
        color: '#f8f9fa',
        padding: '20px',
        textAlign: 'center',
        fontSize: '14px',
        marginTop: '40px'
      }}>
        J9 AI Assistant RAG Management © 2025
      </div>
    </div>
    </>
  );
};

export default RagManagement;